import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IPaginatedResult } from '@shared/interfaces/mailing/IPaginatedResult';
import { IItemBase, IResponsible, IUser } from '@shared/interfaces';
import { PageEvent } from '@angular/material/paginator';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IQueryOrganisation } from '@shared/interfaces/queries/IQueryOrganisation';
import { OrganisationService } from '@shared/services/organisation.service';
import { SnackService } from '@shared/services';
import { IUnPublishMass } from '@shared/interfaces/responsible/IStartReportingPeriod';
import { disableBackgroundScrolling, distinctArray, enableBackgroundScrolling } from '@shared/helpers/helpers';

@Component({
    selector: 'app-org-mass-unpublish',
    templateUrl: './org-mass-unpublish.component.html',
    styleUrls: ['./org-mass-unpublish.component.scss'],
    standalone: false
})
export class OrgMassUnpublishComponent implements OnInit, OnDestroy {
    @ViewChild('itemContainer') itemContainer: ElementRef;
    pagedData: IPaginatedResult<IResponsible>;
    event: PageEvent;
    loading: boolean;
    inputText: string;
    formGroup: UntypedFormGroup;
    isSaving: boolean;
    didUnPublishOrgCodes: string[] = [];

    constructor(
        public dialogRef: MatDialogRef<OrgMassUnpublishComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            excluded: IResponsible[];
            multiple: boolean;
            query?: IQueryOrganisation;
        },
        private organisationService: OrganisationService,
        private formBuilder: UntypedFormBuilder,
        private snack: SnackService
    ) {}

    get paginatedData(): IResponsible[] {
        return this.pagedData?.items ?? [];
    }

    get allSelected(): boolean {
        return this.formGroup.get('allSelected').value;
    }

    set allSelected(val: boolean) {
        this.formGroup.get('allSelected').patchValue(val);
    }

    get idsControl(): UntypedFormControl {
        return this.formGroup.get('ids') as UntypedFormControl;
    }

    get ids(): string[] {
        return this.idsControl.value;
    }

    get command(): IUnPublishMass {
        return this.formGroup.value;
    }

    ngOnInit() {
        disableBackgroundScrolling();
        this.loadData();
        this.buildForm();
    }

    ngOnDestroy() {
        enableBackgroundScrolling();
    }

    buildForm() {
        this.formGroup = this.formBuilder.group({
            allSelected: false,
            ids: [[]],
            orgCode: this.data.query?.orgCode,
            responsibleType: this.data?.query?.responsibleType
        });
    }

    changeSelected(element: IUser) {
        if (!this.data.multiple) {
            this.pagedData.items.forEach(e => (e.selected = false));
            this.removeIds(this.pagedData.items.map(e => e.id));
        }

        element.selected = !element.selected;

        if (element.selected) {
            this.addId(element.id);
        } else {
            this.removeId(element.id);
        }

        if (this.data.multiple) {
            if (this.allSelected && !element.selected) {
                this.allSelected = false;
            } else if (!this.allSelected && !this.pagedData.hasMore && !this.pagedData.items.some(e => !e.selected)) {
                this.allSelected = true;
                this.clearIds();
            }

            if (!this.allSelected && !this.ids.length && this.pagedData.items.some(e => e.selected)) {
                this.addIds(this.pagedData.items.map(e => e.id));
            }
        }
    }

    selectAll() {
        if (!this.data.multiple) {
            return;
        }

        this.pagedData.items.forEach(e => (e.selected = this.allSelected));

        if (this.allSelected) {
            this.clearIds();
        } else {
            this.removeIds(this.pagedData.items.map(e => e.id));
        }
    }

    async loadData(event?: PageEvent) {
        if (
            !this.pagedData ||
            !event ||
            (this.pagedData &&
                event.pageIndex > this.pagedData.page &&
                (event.pageIndex + 1) * event.pageSize > this.pagedData.items.length)
        ) {
            const query: IQueryOrganisation = {
                ...this.data?.query,
                query: this.inputText ?? null,
                excludeUnPublished: true,
                pagination: {
                    page: event?.pageIndex ?? 0,
                    pageSize: 50
                }
            };

            const paginatedResult = await this.organisationService.getOrganisationFlat(query);
            const data = paginatedResult.items;
            if (this.data?.excluded?.length) {
                data.forEach(e => ((e as IItemBase).disabled = this.data?.excluded.some(a => a.id === e.id)));
            }

            if (this.data?.multiple && this.allSelected) {
                data.forEach(e => ((e as IItemBase).selected = true));
            }

            if (event) {
                this.pagedData = {
                    ...paginatedResult,
                    items: [...(this.pagedData?.items ?? []), ...paginatedResult.items]
                };
            } else {
                this.pagedData = paginatedResult;
                setTimeout(() => this.setScrollListener(), 50);
            }

            if (!this.pagedData?.items?.length) {
                this.dialogRef.close();
            }
        }

        this.event = event ?? ({ pageIndex: 0, pageSize: 20 } as PageEvent);
    }

    addId(val: string) {
        this.idsControl.patchValue(distinctArray([...this.ids, val]));
    }

    addIds(val: string[]) {
        this.idsControl.patchValue(distinctArray([...this.ids, ...val]));
    }

    removeId(val: string) {
        this.idsControl.patchValue(distinctArray(this.ids.filter(e => e !== val)));
    }

    removeIds(val: string[]) {
        this.idsControl.patchValue(distinctArray(this.ids.filter(e => !val.includes(e))));
    }

    clearIds() {
        this.idsControl.patchValue([]);
    }

    protected async unPublishMass(): Promise<void> {
        if (!this.allSelected && !this.ids.length) {
            this.snack.openSnackBarError('You need to select at least one item.');
            return;
        }

        this.isSaving = true;

        try {
            const result = await this.organisationService.unPublishMass(this.command);
            this.snack.openSnackBarSuccess('Successfully unpublished report(s).');
            this.didUnPublishOrgCodes.push(...result);
            this.close();
        } catch (error) {
            this.snack.openSnackBarError('Failed to unpublish report(s): ' + error);
        }

        this.isSaving = false;
    }

    protected close(): void {
        this.dialogRef.close(this.didUnPublishOrgCodes);
    }

    private setScrollListener() {
        const itemContainer = this.itemContainer?.nativeElement;
        if (itemContainer) {
            itemContainer.onscroll = async () => {
                if (this.pagedData.hasMore) {
                    const pos = (itemContainer.scrollTop || itemContainer.scrollTop) + itemContainer.offsetHeight;
                    const max = itemContainer.scrollHeight;

                    if (pos >= max - 1 && !this.loading) {
                        this.loading = true;
                        this.event.pageIndex++;
                        await this.loadData(this.event);
                        this.loading = false;
                    }
                }
            };
        }
    }
}
